import type {
  Fees,
  PasskeyCredential,
  TelegramStatus,
  UserActivity,
  UserProfile,
} from '@/lib/api/auth/models';
import type {
  FeesDto,
  PasskeyCredentialDto,
  TelegramStatusResponseDto,
  UserActivityDto,
  UserProfileDto,
  VerifyUser2FAResponseDto,
} from '@/lib/api/auth/types';
import { mapPlayerLook } from '@/lib/api/rewards-hub/mappers';
import type { MappedAuthData } from '@/lib/auth/responseMapper';

export function mapUserActivity(dto: UserActivityDto): UserActivity {
  const toDeviceType = (raw: string): UserActivity['deviceType'] => {
    const normalized = (raw || '').toUpperCase();
    if (normalized === 'DESKTOP' || normalized === 'MOBILE' || normalized === 'TABLET') {
      return normalized;
    }
    return 'DESKTOP';
  };

  const toCountryName = (code: string): string => {
    const overrides: Record<string, string> = {
      RU: 'Russia',
    };
    const upper = (code || '').toUpperCase();
    if (overrides[upper]) return overrides[upper];
    try {
      // Use English names by default
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
      return displayNames.of(upper) || upper;
    } catch {
      return upper;
    }
  };

  return {
    id: dto.d,
    deviceLabel: dto.l,
    deviceType: toDeviceType(dto.t),
    ip: dto.i,
    geoCountry: toCountryName(dto.c),
    createdAt: dto.a,
    lastSeenAt: dto.s,
    active: Boolean(dto.v),
  };
}

export function mapVerifyUser2FA(dto: VerifyUser2FAResponseDto): MappedAuthData {
  return {
    token: dto.t,
    user: {
      id: 'temp-id',
      email: dto.e,
      name: dto.n,
      confirmed: dto.c,
      userGroup: undefined,
      language: dto.l,
      activityId: dto.a,
      twoFactorEnabled: dto.o,
    },
    confirmed: dto.c,
  };
}

function toNumberOrZero(value: unknown): number {
  const num = Number(value);
  return Number.isNaN(num) || !Number.isFinite(num) ? 0 : num;
}

export function mapFees(dto: FeesDto): Fees {
  return {
    buyTakerFee: toNumberOrZero(dto.btf),
    sellTakerFee: toNumberOrZero(dto.stf),
    buyMakerFee: toNumberOrZero(dto.bmf),
    sellMakerFee: toNumberOrZero(dto.smf),
  };
}

export function mapUserProfile(dto: UserProfileDto): UserProfile {
  return {
    nickname: dto.n,
    email: dto.e,
    locale: dto.l,
    fees: dto.f ? mapFees(dto.f) : undefined,
    // Identity fields from cookie-based session (Task 5+)
    id: dto.i,
    confirmed: dto.c,
    userGroup: dto.g,
    activityId: dto.a,
    twoFactorEnabled: dto.o,
    look: mapPlayerLook(dto.lk),
  };
}

export function mapTelegramStatus(dto: TelegramStatusResponseDto): TelegramStatus {
  return {
    linked: dto.l,
    username: dto.n,
  };
}

export function mapPasskeyCredential(dto: PasskeyCredentialDto): PasskeyCredential {
  return {
    id: dto.id,
    label: dto.label,
    createdAt: dto.createdAt,
    lastUsedAt: dto.lastUsedAt,
    backedUp: dto.backedUp,
    transports: dto.transports ? dto.transports.split(',').filter(Boolean) : [],
  };
}
